import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchEntitlement } from '../../../../store/actions/entitlementActions';
import { Btn, ErrorState, Loading } from '../../../ForBrands/ui/BrandUI';
import { MobileStackHeader } from '../../../ForBrands/ui/mobile/MobileShell';
import { HubGroup, SectionRow } from '../../../ForBrands/ui/mobile/MobileUI';
import { HELP_ACTION } from '../../../ForBrands/ui/mobile/support';
import { NavIcon } from '../../../ForAthletes/v2/edit/editHubPieces';
import { COACH_VERIFICATION_PATH } from '../coachPaths';
import { GROUPS, PLAN_LOADING, SECTIONS, planRows, sectionSummary } from './coachProfileModel';
import { shareCoachCode } from './coachProfileServices';
import { KeyValueCard, KeyValueRow } from './coachProfilePieces';
import VerificationCard from './VerificationCard';
import AccountSection from './sections/AccountSection';
import TeamSettingsSection from './sections/TeamSettingsSection';
import AdditionalTeamsSection from './sections/AdditionalTeamsSection';

// "Profile & verification" on phones (Figma COACH MODE V2 mobile, EDIT
// PROFILE - MOBILE 2471:3688, "Hub model (v2)"). /coach/profile is a stack
// route: the Coach Mode shell draws no chrome, so every screen here brings
// its own header (back, title, Help).
//  - No `section` param (or an unknown one) shows the hub (00 pending
//    2471:3689, 00a verified 2471:4290): the verification card, then the
//    sections under their group overlines, each with a support line.
//  - `?section=` is a pushed screen (01 to 04). Back returns to the hub,
//    saving a pending name on the way; the Account screen's Done waits for
//    the save and stays open when it is refused.
//  - Back on the hub leaves the profile: to the awaiting verification screen
//    while the coach awaits verification (in place of the profile, so the
//    history does not loop between the two), else to the page before (or
//    Home). Back over the loading or error state of a section goes to the hub.
//  - When the screen changes, focus moves to the new screen's (visually
//    hidden) h1.
//  - The plan is loaded once, when these screens mount: the hub's
//    Subscription line and the Subscription screen both read it.
// CoachProfileHub loads the profile and keeps the name form; these are its
// phone screens.

export const HUB_TITLE = 'Profile & verification';
const HOME_PATH = '/coach/home';

// Room under the last field: the fixed AutosaveBar on Account, the home
// indicator everywhere.
const BODY_BOTTOM = {
  default: 'pb-[calc(32px+env(safe-area-inset-bottom))]',
  bar: 'pb-[calc(150px+env(safe-area-inset-bottom))]',
};

const scrollTop = () => {
  try {
    window.scrollTo({ top: 0 });
  } catch {
    /* no scrolling here */
  }
};

// Whether "back" can step back to a page of this app before the profile.
// The browser router numbers its history entries (idx 0 is the first page
// of the visit); without that (a memory router) it goes by whether the
// profile was the first entry, which the router keys "default".
const canStepBack = (enteredFirst) => {
  const idx = typeof window !== 'undefined' ? window.history?.state?.idx : undefined;
  if (typeof idx === 'number') return idx > 0;
  return !enteredFirst;
};

// Back on the hub (and over its loading and error states). A coach who
// awaits verification goes to the awaiting screen, the only Coach Mode page
// open to them; everyone else goes back to the page they came from, or to
// Home when the profile opened first (a shared or typed link).
// The awaiting screen takes the profile's place in the history: pushing it
// would leave [awaiting, profile, awaiting], and the browser's back would
// bounce between the two.
export const useProfileExit = (pending) => {
  const navigate = useNavigate();
  const location = useLocation();
  const enteredFirst = useRef(!location?.key || location.key === 'default');
  return () => {
    if (pending) navigate(COACH_VERIFICATION_PATH, { replace: true });
    else if (canStepBack(enteredFirst.current)) navigate(-1);
    else navigate(HOME_PATH);
  };
};

// Back to the hub from a section screen (or its loading and error states): a
// section pushed from the hub pops, so the browser's back agrees; one opened
// from a link replaces itself with the hub.
export const useHubReturn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setParams] = useSearchParams();
  return () => {
    if (location.state?.fromHub) navigate(-1);
    else setParams({}, { replace: true });
    scrollTop();
  };
};

// The stack header over a phone screen, then its body with the 24px gutter
// and the 56px fields of .bm-phone-form. The title is also the page's
// (visually hidden) heading, which `headingRef` can focus.
export const PhoneProfileFrame = ({ title, onBack, bottom = 'default', headingRef, children }) => (
  <div className="flex w-full flex-col bg-white">
    <MobileStackHeader leading="back" onLeading={onBack} title={title} action={HELP_ACTION} />
    <div className={`bm-phone-form flex flex-col px-6 pt-5 ${BODY_BOTTOM[bottom] || BODY_BOTTOM.default}`}>
      <h1 ref={headingRef} tabIndex={-1} className="sr-only">
        {title}
      </h1>
      {children}
    </div>
  </div>
);

// The phone screen's title for the current `section` param.
export const phoneTitleFor = (sectionId) => SECTIONS.find((s) => s.id === sectionId)?.label || HUB_TITLE;

// Loading and error screens: the header first, so there is always a way back.
export const PhoneProfileState = ({ title, onBack, failed, onRetry }) => (
  <PhoneProfileFrame title={title} onBack={onBack}>
    {failed ? (
      <div className="flex flex-col gap-4 pt-5">
        <ErrorState>We could not load your profile.</ErrorState>
        <Btn variant="secondary" size="lg" className="w-full" onClick={onRetry}>
          Try again
        </Btn>
      </div>
    ) : (
      <Loading label="Loading your profile..." />
    )}
  </PhoneProfileFrame>
);

// 00 / 00a · Hub: ProfileSectionRow · Mobile rows (2080:979, the Setting
// variant: no ring) in HubGroups, each with its summary line (D-c6).
const PhoneHub = ({ profile, summaryOf, onOpen, onBack, headingRef }) => (
  <PhoneProfileFrame title={HUB_TITLE} onBack={onBack} headingRef={headingRef}>
    <div className="flex flex-col gap-[22px]">
      <VerificationCard verification={profile.verification} team={profile.team} teamName={profile.team?.name || ''} onSend={shareCoachCode} />
      {GROUPS.map((group) => {
        const rows = SECTIONS.filter((s) => s.group === group);
        return (
          <HubGroup key={group} label={group}>
            {rows.map((s, i) => (
              <SectionRow
                key={s.id}
                icon={<NavIcon src={s.icon} active={false} />}
                title={s.label}
                support={summaryOf(s.id)}
                onClick={() => onOpen(s.id)}
                last={i === rows.length - 1}
              />
            ))}
          </HubGroup>
        );
      })}
    </div>
  </PhoneProfileFrame>
);

const PLAN_LOADING_ROWS = [{ label: 'Status' }, { label: 'Plan' }];

// 04 · Subscription on a phone (2471:4187): the phone look of
// SubscriptionSection (the plan card, then a full-width Manage billing),
// reading the plan these screens loaded instead of loading it a second time.
const PhoneSubscription = ({ entitlement, subscription }) => (
  <>
    <KeyValueCard>
      {(planRows(entitlement || {}, subscription) || PLAN_LOADING_ROWS).map((row) => (
        <KeyValueRow key={row.label} label={row.label}>
          {row.value ?? <span className="bm-body-m text-bm-muted">{PLAN_LOADING}</span>}
        </KeyValueRow>
      ))}
    </KeyValueCard>
    <Btn variant="secondary" size="lg" className="w-full" to="/coach/billing">
      Manage billing
    </Btn>
  </>
);

const CoachProfilePhone = ({ profile, account, params, setParams, onLeave }) => {
  const dispatch = useDispatch();
  const entitlement = useSelector((s) => s.entitlement);
  const subscription = useSelector((s) => s.user?.user?.subscription);
  const goHub = useHubReturn();
  const [doneBusy, setDoneBusy] = useState(false);
  const alive = useRef(true);
  const current = SECTIONS.find((s) => s.id === params.get('section')) || null;
  const screen = current?.id || 'hub';
  const screenRef = useRef(screen);
  const headingRef = useRef(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // The plan, once for all these screens: the hub's Subscription line and
  // the Subscription screen (the desktop loads it in its Subscription section).
  useEffect(() => {
    Promise.resolve(dispatch(fetchEntitlement())).catch(() => {});
  }, [dispatch]);

  // A new screen (a section pushed, or the hub again): focus its heading so
  // screen readers announce it. The first screen keeps the page's focus.
  useLayoutEffect(() => {
    const before = screenRef.current;
    screenRef.current = screen;
    if (before !== screen) headingRef.current?.focus({ preventScroll: true });
  }, [screen]);

  // A section remembers it was pushed from the hub, so going back pops it
  // (and the browser's back agrees); a section opened from a link replaces
  // itself with the hub (useHubReturn).
  const open = (id) => {
    if (account.isDirty()) account.save();
    setParams({ section: id }, { state: { fromHub: true } });
    scrollTop();
  };
  // Back on a section: the name saves on the way (as switching sections does
  // on desktop); the form stays mounted, so a refusal shows on return.
  const backToHub = () => {
    if (account.isDirty()) account.save();
    goHub();
  };
  // Done (AutosaveBar): once the server has the name, back to the hub; a
  // refused save keeps the section open with the reason in the bar.
  const done = async () => {
    if (doneBusy) return;
    setDoneBusy(true);
    const ok = await account.flush();
    if (!alive.current) return;
    setDoneBusy(false);
    if (ok) goHub();
  };

  if (!current) {
    return (
      <PhoneHub
        profile={profile}
        summaryOf={(id) => sectionSummary(id, profile, entitlement, { subscription })}
        onOpen={open}
        onBack={onLeave}
        headingRef={headingRef}
      />
    );
  }

  const body = {
    account: <AccountSection account={profile.account} form={account} onDone={done} doneBusy={doneBusy} />,
    team: <TeamSettingsSection team={profile.team} />,
    additional: <AdditionalTeamsSection teams={profile.additionalTeams} leagueAdmin={profile.leagueAdmin} />,
    subscription: <PhoneSubscription entitlement={entitlement} subscription={subscription} />,
  }[current.id];

  return (
    <PhoneProfileFrame
      title={current.label}
      onBack={backToHub}
      bottom={current.id === 'account' ? 'bar' : 'default'}
      headingRef={headingRef}
    >
      <section aria-label={current.label} className="flex flex-col gap-6">
        {body}
      </section>
    </PhoneProfileFrame>
  );
};

export default CoachProfilePhone;
